import { SignIn } from "@clerk/clerk-react";
import { ArrowRight, CalendarDays, Check, FileText, KeyRound, Palette } from "lucide-react";
import { productFullName, productName } from "../../productIdentity";

const clerkApplicationUrl = typeof window === "undefined" ? "/" : window.location.origin;

const clerkAppearance = {
  variables: { fontFamily: "'Archivo', ui-sans-serif, system-ui, sans-serif" },
  elements: {
    rootBox: "ops-auth-clerk-root", cardBox: "ops-auth-clerk-card-box", card: "ops-auth-clerk-card",
    header: "ops-auth-clerk-header", footer: "ops-auth-clerk-footer", formButtonPrimary: "ops-auth-clerk-submit",
    formFieldLabel: "ops-auth-clerk-label", formFieldInput: "ops-auth-clerk-input", socialButtonsBlockButton: "ops-auth-clerk-social",
    dividerLine: "ops-auth-clerk-divider", dividerText: "ops-auth-clerk-divider-text",
    formFieldInputShowPasswordButton: "ops-auth-clerk-password-toggle", formFieldErrorText: "ops-auth-clerk-error",
  },
  layout: { socialButtonsPlacement: "top" as const, socialButtonsVariant: "blockButton" as const },
};

const lifecycle = [
  { label: "Planned", detail: "Calendar locked", icon: CalendarDays, tone: "plum" },
  { label: "Briefed", detail: "Creative direction", icon: FileText, tone: "plum" },
  { label: "Designed", detail: "Artwork ready", icon: Palette, tone: "lime" },
  { label: "Approved", detail: "Client decision", icon: Check, tone: "lime" },
];

const contentRows = [
  { title: "Weeknight pasta", format: "IG · Post", owner: "LM", status: "Planned", tone: "plum" },
  { title: "September essentials", format: "FB · Carousel", owner: "JW", status: "Briefed", tone: "plum" },
  { title: "Five-ingredient dinner", format: "TT · Reel", owner: "AK", status: "Designed", tone: "lime" },
  { title: "Autumn table", format: "IG · Story", owner: "LM", status: "Approved", tone: "lime" },
];

export function UnauthenticatedLandingPage() {
  return (
    <div className="ops-login-page">
      <header className="ops-login-header">
        <a className="ops-login-brand" href="#workspace-access" aria-label={`${productFullName} home`}>
          <span className="ops-login-brand-mark" aria-hidden="true"><span /></span>
          <span><strong>{productName}</strong><small>Agency-ready content planning workspace</small></span>
        </a>
        <span className="ops-login-access-label"><KeyRound size={15} aria-hidden="true" /> Invite-only workspace</span>
      </header>

      <main className="ops-login-main">
        <section className="ops-login-intro" id="workspace-access" aria-labelledby="ops-login-title">
          <div className="ops-login-copy">
            <h1 id="ops-login-title">Turn days of coordination into one clear plan.</h1>
            <p>
              {productName} automates the repetitive work behind social calendars—from planning
              and briefing to creative review and client approval—so your team can spend more time
              on the ideas that move the brand forward.
            </p>
          </div>

          <div className="ops-login-auth-shell">
            <div className="ops-login-auth-heading"><h2>Welcome back</h2><p>Sign in to continue to your workspace.</p></div>
            <div className="ops-login-clerk">
              <SignIn routing="hash" signInUrl={clerkApplicationUrl} forceRedirectUrl="/" appearance={clerkAppearance} />
            </div>
            <p className="ops-login-auth-note"><KeyRound size={15} aria-hidden="true" />Use the address connected to your workspace invitation.</p>
          </div>
        </section>

        <section className="ops-login-workspace" aria-labelledby="ops-login-workspace-title">
          <span className="ops-login-month-ghost" aria-hidden="true">09</span>
          <div className="ops-login-workspace-sheet">
            <header className="ops-login-workspace-head">
              <div><span className="ops-login-file-icon" aria-hidden="true"><FileText size={19} /></span><div><h2 id="ops-login-workspace-title">Wiley Gourmet</h2><p>September 2026 · Demo workspace</p></div></div>
              <span className="ops-login-open-label">Month in progress</span>
            </header>
            <ol className="ops-login-lifecycle" aria-label="Content production lifecycle">
              {lifecycle.map(({ label, detail, icon: Icon, tone }, index) => (
                <li key={label}><span className={`ops-login-stage-icon ${tone}`}><Icon size={17} aria-hidden="true" /></span><div><strong>{label}</strong><small>{detail}</small></div>{index < lifecycle.length - 1 ? <ArrowRight className="ops-login-stage-arrow" size={16} aria-hidden="true" /> : null}</li>
              ))}
            </ol>
            <div className="ops-login-content-table" role="table" aria-label="Sample September content workflow">
              <div className="ops-login-content-header" role="row"><span role="columnheader">Content</span><span role="columnheader">Channel</span><span role="columnheader">Owner</span><span role="columnheader">Status</span></div>
              {contentRows.map((row) => (
                <div className="ops-login-content-row" role="row" key={row.title}><strong role="cell">{row.title}</strong><span role="cell">{row.format}</span><span role="cell" className="ops-login-owner">{row.owner}</span><span role="cell" className={`ops-login-status ${row.tone}`}>{row.status}</span></div>
              ))}
            </div>
          </div>
          <p className="ops-login-sample-note">Illustrative data from the Wiley Gourmet demo workspace.</p>
        </section>
      </main>
    </div>
  );
}
